import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject, signal } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { GlobalEventNameEnum } from 'projects/fce-components/src/app/core/services/global-events/global-events.types'
import { Subject, filter, fromEvent, merge, takeUntil } from 'rxjs'
import { TreeViewService } from './tree-view.service'
import { TreeViewItem, TreeViewItemTypeEnum } from './tree-view.type'
import { fadeInOut, listAnimation } from '../../../../../../core/animations'
import { AssetsTreeService } from '../../../../services/assets-tree.service'
import { WarrantyStatusLabelComponent } from '../../../details-section/components/warranty-status-label/warranty-status-label.component'

@Component({
  selector: 'tree-view',
  templateUrl: './tree-view.component.html',
  styleUrls: ['./tree-view.component.scss'],
  animations: [
    fadeInOut,
    listAnimation
  ],
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    NgScrollbarModule,
    ReactiveFormsModule,
    TranslateModule,
    WarrantyStatusLabelComponent
  ]
})
export class TreeViewComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  public item!: TreeViewItem

  @Input({ required: true })
  public level!: number

  @Input({ required: false })
  public service?: any

  public isOpened = signal<boolean>(false)
  public isLoading = signal<boolean>(false)
  public isSelected = signal<boolean>(false)
  public children = signal<TreeViewItem[]>([])
  public showArrowButton = signal<boolean>(true)

  private static openedNodeIds: Set<string> = new Set()
  private static selectedNodeId: string | null = null

  private readonly destroy$ = new Subject<void>()
  private readonly cdf = inject(ChangeDetectorRef)
  private readonly treeViewService = inject(TreeViewService)
  public readonly assetsTreeService = inject(AssetsTreeService)

  public ngOnInit(): void {
    this.children.set(this.item.children || [])
    if (!this.item.isParent && !this.children().length) {
      this.showArrowButton.set(false)
    }

    if (TreeViewComponent.openedNodeIds.has(this.item.id)) {
      this.isOpened.set(true)
      void this.loadChildren()
    }

    if (TreeViewComponent.selectedNodeId === this.item.id) {
      this.isSelected.set(true)
    }

    this.treeViewService.closeAllNodes$
      .pipe(
        filter((level) => level === this.level),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.isOpened.set(false)
        this.isSelected.set(false)
      })

    this.treeViewService.selectNode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedNode) => {
        this.isSelected.set(selectedNode?.id === this.item.id)
        if (selectedNode?.id === this.item.id && selectedNode.showDetails) {
          this.assetsTreeService.selectedItem.set(this.item)
          this.assetsTreeService.showDetails$.next(this.item)
        }
      })

    this.assetsTreeService.closeDetails$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isSelected.set(false)
        if (!this.item.children?.length) {
          this.isOpened.set(false)
        }
      })

    this.treeViewService.openChildren$
      .pipe(
        filter((id) => this.item.id === id),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isOpened.set(true)
      })

    merge(
      fromEvent(window, GlobalEventNameEnum.Created),
      fromEvent(window, GlobalEventNameEnum.Updated)
    )
      .pipe(
        filter(() => this.isOpened() && this.item.type === TreeViewItemTypeEnum.Location),
        takeUntil(this.destroy$)
      )
      .subscribe(async (event: any) => {
        const details = event.detail || {}

        if (details.location?.id === this.item.id && details?.type === TreeViewItemTypeEnum.Location) {
          this.item.name = details.location.name
          this.item.archived = details.location.archived

          await this.loadChildren()
        }

        if (details?.type === TreeViewItemTypeEnum.Equipment) {
          const { equipment, isUpdatedFromParentDetails, isCreatedFromEmptyState } = details

          const isFromNewLocation = equipment?.location?.id !== this.item.id
          if (isFromNewLocation && isCreatedFromEmptyState) {
            return
          }

          await this.loadChildren()
          this.cdf.detectChanges()

          const parent = this.assetsTreeService.selectedItem()
          if (isUpdatedFromParentDetails && parent) {
            if (parent.type === TreeViewItemTypeEnum.LocationSector) {
              this.treeViewService.openChildren$.next(parent.id)
            }

            this.cdf.detectChanges()
            if (parent.type === TreeViewItemTypeEnum.LocationEnvironment) {
              this.treeViewService.openChildren$.next(parent.original.locationSector.id)

              this.cdf.detectChanges()
              this.treeViewService.openChildren$.next(parent.id)
            }

            this.assetsTreeService.selectedItem.set(parent)
            this.treeViewService.selectNode$.next({ id: parent.id })
            return
          }

          if (isFromNewLocation) {
            this.assetsTreeService.selectedItem.set(this.item)
            this.treeViewService.selectNode$.next({ id: this.item.id })
            return
          }

          if (equipment?.locationSector) {
            this.treeViewService.openChildren$.next(equipment.locationSector.id)
          }

          this.cdf.detectChanges()
          if (equipment?.locationEnvironment) {
            this.treeViewService.openChildren$.next(equipment.locationEnvironment.id)
          }

          this.cdf.detectChanges()
          this.treeViewService.selectNode$.next({ id: equipment.id, showDetails: true })
        }

        if (details?.type === TreeViewItemTypeEnum.LocationSector) {
          const { locationSector } = details

          const isFromNewLocation = locationSector?.location?.id !== this.item.id
          if (isFromNewLocation || !locationSector) {
            return
          }

          await this.loadChildren()
          this.cdf.detectChanges()
          this.treeViewService.openChildren$.next(locationSector.id)
          this.treeViewService.selectNode$.next({ id: locationSector.id, showDetails: true })
        }

        if (details?.type === TreeViewItemTypeEnum.LocationEnvironment) {
          const { locationEnvironment, isUpdatedFromParentDetails } = details

          await this.loadChildren()
          this.cdf.detectChanges()

          if (!locationEnvironment) {
            return
          }

          const parent = this.assetsTreeService.selectedItem()
          if (isUpdatedFromParentDetails && parent) {
            this.treeViewService.openChildren$.next(parent.id)

            this.cdf.detectChanges()
            this.assetsTreeService.selectedItem.set(parent)
            this.treeViewService.selectNode$.next({ id: locationEnvironment.id, showDetails: true })
            return
          }

          this.cdf.detectChanges()
          if (locationEnvironment?.locationSector) {
            this.treeViewService.openChildren$.next(locationEnvironment.locationSector.id)
            this.cdf.detectChanges()
            this.treeViewService.openChildren$.next(locationEnvironment.id)
          }

          this.cdf.detectChanges()
          this.treeViewService.selectNode$.next({ id: locationEnvironment.id, showDetails: true })
        }
      }
      )
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public trackByFn(index: number): number {
    return index
  }

  public async toggleShowChildren($event: any): Promise<void> {
    $event.stopPropagation()
    const isOpened = this.isOpened()
    this.isOpened.set(!isOpened)

    if (this.isOpened()) {
      await this.loadChildren()
      TreeViewComponent.openedNodeIds.add(this.item.id)
    } else {
      TreeViewComponent.openedNodeIds.delete(this.item.id)
    }
  }

  public async toggleSelect($event: any): Promise<void> {
    $event.stopPropagation()
    const isSelected = this.isSelected()
    this.isSelected.set(!isSelected)

    if (this.isSelected()) {
      TreeViewComponent.selectedNodeId = this.item.id
      this.treeViewService.selectNode$.next({ id: this.item.id, showDetails: true })
      return
    }

    TreeViewComponent.selectedNodeId = null
    this.treeViewService.selectNode$.next(null)
    this.assetsTreeService.selectedItem.set(null)
    this.assetsTreeService.closeDetails$.next()
  }

  private async loadChildren(): Promise<void> {
    if (this.isOpened() && this.item.isParent) {
      this.isLoading.set(true)
      const items = await this.service.load(this.item)
      this.children.set(items)
      this.isLoading.set(false)
      this.item.children = items
      this.showArrowButton.set(Boolean(items.length))
    }
  }
}
